import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, Typo, Flex, ListTable, Button, Avatar, theme, Icon } from '../../ui';
import { useIsIncarnated, useHasRequiredLevel, useIsAdmin, useRefetchOnAuthChange } from '../auth/hooks';
import { useEditableCourses } from './hooks';
import { getPeriodByTime } from '../../utils/ucpPeriods';
import Loading from '../../ui/Loading';
import ConfirmDelete from '../../ui/Modal/ConfirmDelete';
import { useDeleteCourse, useDuplicateCourse } from '../course/hooks';
import CreateCourseModel from '../course/components/CreateCourseModel';
import { useTranslation } from 'react-i18next';
import GenerateGiftCodesModal from './components/GenerateGiftCodesModal';
import PageHeader from '../../ui/Page/PageHeader';
import PageControls from '../../ui/Page/PageControls';
import { Tooltip } from '@mui/material';
import { useFeatures } from '../settings/hooks';
import PageSection from '../../ui/Page/PageSection';

const Courses = () => {
  const deleteCourse = useDeleteCourse();
  const duplicateCourse = useDuplicateCourse();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isIncarnated = useIsIncarnated();
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [savedCurrentPage, setSavedCurrentPage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [generateCodesForCourse, setGenerateCodesForCourse] = useState(false);
  const isOwner = useHasRequiredLevel('owner');
  const isAdmin = useIsAdmin();
  const features = useFeatures();

  const { courses = [], isLoading, error, refetch } = useEditableCourses({ searchText: searchQuery, showUncorrected: true });
  useRefetchOnAuthChange(refetch);

  const deleteCourseConfirmed = async () => {
    await deleteCourse(courseToDelete.id);
    setCourseToDelete(null);
  };

  const duplicateCourseHandler = async (obj) => {
    const data = await duplicateCourse(obj.id);
    if (data) openSettings(data);
  };

  const handleCourseCreated = (data) => {
    if (data) openSettings(data);
  };

  const openModal = () => setIsModalOpen(true);

  const closeModal = () => {
    setIsModalOpen(false);
    setGenerateCodesForCourse(null);
  };

  const openTasks = useCallback(
    (obj) => {
      navigate(`/courses/${obj.id}/tasks`);
    },
    [navigate],
  );
  const openUsers = useCallback(
    (obj) => {
      navigate(`/courses/${obj.id}/users`);
    },
    [navigate],
  );
  const openQuizes = useCallback(
    (obj) => {
      navigate(`/courses/${obj.id}/quiz`);
    },
    [navigate],
  );
  const openSettings = useCallback(
    (obj) => {
      navigate(`/courses/${obj.id}/settings`);
    },
    [navigate],
  );
  const openCourse = useCallback(
    (obj) => {
      navigate(`/courses/${obj.id}`);
    },
    [navigate],
  );

  const [tableInfo, setTableInfo] = useState(
    [
      {
        label: t('globals.name'),
        hasSorting: { sortedMethod: 'string', propVal: 'name' },
        data: (item) => {
          return (
            <Flex gap='10' $wrap='wrap'>
              <Avatar name={item.name} size='small'>
                {item.name.substr(0, 1)}
              </Avatar>
              <div>
                <Typo.Text>{item.name}</Typo.Text>
                <Typo.SmallText>{`${t('course.courseId')}: ${item.id}`}</Typo.SmallText>
              </div>
            </Flex>
          );
        },
      },
      { label: t('course.createdAt'), hasSorting: { sortedMethod: 'date', propVal: 'created' }, data: (item) => item.created && item.created.split(' ')[0] },
      features.draftCourses && {
        label: t('course.status'),
        data: (item) =>
          item.draft ? (
            <Tooltip title={t('globals.draft')} placement={'bottom'}>
              {' '}
              <Icon.InConstruction color={theme.colors.NEU_600} />{' '}
            </Tooltip>
          ) : (
            <Tooltip title={t('globals.ready')} placement={'bottom'}>
              {' '}
              <Icon.Published size='small' color={theme.colors.NEU_600} />{' '}
            </Tooltip>
          ),
      },
      { label: t('course.activeUsers'), hasSorting: { sortedMethod: 'number', propVal: 'studentCount' }, data: (item) => item.studentCount },
      { label: t('course.accessDuration'), data: (item) => t(`course.${getPeriodByTime(item.ucpPeriod)?.label}`) },
      {
        label: t('course.uncorrectedTasks'),
        defaultSorting: true,
        hasSorting: { sortedMethod: 'number', propVal: 'uncorrectedCount', initialOrder: 'desc' },
        data: (item) => item.uncorrectedCount,
      },
      // { label: t('globals.quiz') },
      // { label: 'Discussions' },
      // { label: 'Pages' },
      // {
      //   label: 'Publish Status', data: (item) => (
      //     <>
      //       <Icon.InConstruction color={theme.colors.NEU_600} />
      //       <Icon.Published color={theme.colors.NEU_600} />
      //       {/* If published what type? buy invitation or in store  */}
      //       <Icon.Storefront size='small' color={theme.colors.NEU_600} />
      //       <Icon.ByInvitation size='small' color={theme.colors.NEU_600} />
      //     </>
      //   )
      // },
      {
        label: 'Actions',
        menuOptions: () =>
          [
            { label: t('globals.users'), onClick: openUsers },
            { label: t('globals.tasks'), onClick: openTasks },
            { label: t('globals.quiz'), onClick: openQuizes },
            { label: t('globals.settings'), onClick: openSettings },
            isOwner && { label: t('globals.duplicate'), onClick: duplicateCourseHandler },
            { label: t('course.createGiftCodes'), onClick: setGenerateCodesForCourse },
            isOwner && { label: t('globals.delete'), onClick: (item) => setCourseToDelete(item), variant: 'action' },
          ].filter(Boolean),
      },
    ].filter(Boolean),
  );

  const handleSearch = useCallback((value) => {
    setSearchQuery(value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  return (
    <Page
      staticElements={
        <PageHeader>
          <PageControls
            onClearSearch={clearSearch}
            onSearch={handleSearch}
            placeholder={t('course.searchPlaceholder')}
            disableClear={!searchQuery}
            openModelButton={
              <Flex gap='10'>
                {(isAdmin || isIncarnated) && <Button onClick={setGenerateCodesForCourse}>{t('course.createGiftCodes')}</Button>}
                {isOwner && (
                  <Button icon='Edit' onClick={openModal}>
                    {t('course.createCourse')}
                  </Button>
                )}
              </Flex>
            }
          />
        </PageHeader>
      }
    >
      {isLoading ? (
        <Loading />
      ) : (
        <PageSection>
          <ListTable
            tableInfo={tableInfo}
            data={courses}
            onRowClick={openCourse}
            tableName={'courses'}
            tableSetter={setTableInfo}
            keepTrackOfPage={setSavedCurrentPage}
            defaultStartPage={savedCurrentPage}
          />
        </PageSection>
      )}
      <ConfirmDelete
        onClose={setCourseToDelete}
        onAcceptDelete={deleteCourseConfirmed}
        open={!!courseToDelete?.id}
        description={t('course.confirmDeleteCourseDescription', { courseToDelete: courseToDelete?.name })}
      />
      <GenerateGiftCodesModal course={generateCodesForCourse} onClose={closeModal} isAdmin={isAdmin || isIncarnated} />
      <CreateCourseModel open={isModalOpen} onClose={closeModal} onSuccess={handleCourseCreated} />
    </Page>
  );
};

export default Courses;
